import { Box, Button, Divider, Typography } from "@mui/material";
import { buttonFileStyle } from "shared/lib/style";
import style from "./loadFile.module.scss";
import fileIcon from "assets/images/myDocument/file.svg";
import btnIcon from "assets/images/myDocument/btn_icon.svg";
import fileImg from "assets/images/admin/file.svg";
import trashRedImg from "assets/images/admin/trash_red.svg";
import { ICreateMarathon } from "features/createEditMarathon/model/types";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import { PlusIcon } from "assets/icons";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "shared/ui/ErrorMessage/ErrorMessage";
import { toast } from "react-toastify";

interface ILoadDocumentProps {
  data: ICreateMarathon;
  removeDocument: (index: number) => void;
  append: (file: File | string | null) => void;
}

export const LoadDocument: React.FC<ILoadDocumentProps> = ({
  data,
  removeDocument,
  append,
}) => {
  const {
    formState: { errors },
  } = useFormContext<ICreateMarathon>();
  const [isDragOpen, setIsDragOpen] = useState(false);
  const [fileError, setFileError] = useState<string>("");
  const { t } = useTranslation();

  const getFileName = (file: any) => {
    if (typeof file === "string") {
      const path = file.replace(/\\/g, "/");

      const uuidPattern =
        /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/g;
      const uuids = path.match(uuidPattern);

      if (uuids && uuids.length > 0) {
        const lastUuid = uuids[uuids.length - 1];
        const lastUuidIndex = path.lastIndexOf(lastUuid);

        if (lastUuidIndex !== -1) {
          const afterUuid = path.substring(lastUuidIndex + lastUuid.length);

          const cleanFileName = afterUuid.replace(/^[.\/]+/, "");

          if (cleanFileName) {
            return cleanFileName;
          }
        }
      }

      const lastDotIndex = path.lastIndexOf(".");
      if (lastDotIndex !== -1) {
        const lastSlashIndex = path.lastIndexOf("/", lastDotIndex);
        if (lastSlashIndex !== -1) {
          return path.substring(lastSlashIndex + 1);
        }
      }

      return path.split("/").pop();
    } else if (
      file &&
      typeof file === "object" &&
      file !== null &&
      "name" in file
    ) {
      return file.name;
    }
    return;
  };

  const getFileSize = (file: any) => {
    if (file && typeof file === "object" && "size" in file) {
      const size = file.size;
      return size < 1048576
        ? `${(size / 1024).toFixed(1)} KB`
        : `${(size / 1048576).toFixed(1)} MB`;
    }
    return "";
  };

  const validateFile = (file: File): string | null => {
    const allowedExt = ["pdf", "docx"];
    const ext = file.name.split(".").pop()?.toLowerCase();

    if (!ext || !allowedExt.includes(ext)) {
      return "Разрешены только файлы формата PDF и DOCX";
    }

    if (file.size > 20 * 1024 * 1024) {
      return "Размер файла не должен превышать 20 МБ";
    }

    return null;
  };

  const handleFileChange = (file: File) => {
    const error = validateFile(file);
    setFileError(error || "");

    if (!error) {
      append(file);
      toast.success("Файл успешно загружен");
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: "16px",
        }}
      >
        <Typography
          sx={{ fontSize: "16px", lineHeight: "24px", fontFamily: "Raleway" }}
        >
          {t("createMarathon.documents")}
        </Typography>
        <Divider sx={{ flex: "1", background: "#ECF2F7" }} />
      </Box>
      <label className={`label-personalCabinet ${style.label}`} htmlFor="-">
        {t("createMarathon.file")}
        {data.documents.length ? (
          ""
        ) : (
          <div
            className={`${style.box_load} ${isDragOpen ? style.box_load_drag : ""}`}
            style={{
              border: fileError || errors.documents?.message ? "2px solid #d4322a" : undefined,
              backgroundColor: fileError || errors.documents?.message ? "rgba(255,0,0,0.05)" : undefined,
            }}
            onDragStart={(e) => {
              e.preventDefault();
              setIsDragOpen(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragOpen(false);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOpen(true);
            }}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) {
                handleFileChange(file);
                setIsDragOpen(false);
              }
            }}
          >
            <img src={fileIcon} alt="Файл" />
            <div className={style.box_load_text}>
              {isDragOpen ? (
                <p>{t("createMarathon.dropToLoad")}</p>
              ) : (
                <>
                  <p>
                    {t("createMarathon.fileDrag1")} <br />{" "}
                    {t("createMarathon.fileDrag2")}
                  </p>
                  <Button
                    variant="contained"
                    component="label"
                    sx={buttonFileStyle}
                    className={style.box_load_btn}
                  >
                    <img src={btnIcon} alt={t("createMarathon.chooseFile")} />
                    <span>{t("createMarathon.chooseFile")}</span>
                    <input
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileChange(e.target.files[0]);
                        }
                      }}
                      accept=".pdf,.docx"
                      hidden
                    />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </label>
      {data.documents.map((file, index) => {
        return (
          <div className={style.main}>
            <div className={style.main__first}>
              <img className={style.main__image} src={fileImg} alt="Файл" />
              <p className={style.main__fileName}>
                {getFileName(file)}
                {getFileSize(file) && <span> {getFileSize(file)}</span>}
              </p>
            </div>
            <div>
              <img
                style={{ cursor: "pointer" }}
                src={trashRedImg}
                alt="Удалить файл"
                onClick={() => removeDocument(index)}
              />
            </div>
          </div>
        );
      })}
      <Box>
        {/* <Box className="globalErrorMessage">{errors.documents?.message}</Box> */}
        <ErrorMessage error={fileError || errors.documents?.message} />
      </Box>
      {data.documents.length ? (
        <label
          style={{
            paddingTop: "16px",
          }}
          className={style.text_add}
        >
          <PlusIcon /> {t("createMarathon.addDocument")}
          <input
            type="file"
            accept=".pdf,.docx"
            onChange={(e) => {
              if (e.target.files) {
                const file = e.target.files[0];
                if (file) {
                  handleFileChange(file);
                }
              }
            }}
            multiple
            hidden
          />
        </label>
      ) : (
        ""
      )}
    </Box>
  );
};

